import "./appliance.scss";

import { PageProps, graphql } from "gatsby";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ProductPreviewGallery from "../components/product/previewGallery";
import { usePageLocalization } from "@sbi-international/boutik.gatsby.plugin.multilocale";
import { Metadata } from "../models/metadata";
import { IProduct, ServicesContext } from "@sbi-international/boutik.gatsby.plugin.services";
import SEO from "../components/Seo";
import { Icon } from "../components/primitives";
import MainLayout from "../components/layout/MainLayout";
import { AddItemButton } from "@sbi-international/boutik.gatsby.plugin.checkout";
import { getAvailability } from "../utils/productAvailabilityInfo";
import { ProductPrice } from "../components/price/ProductPrice";

import { ProductStock } from "@sbi-international/boutik.gatsby.plugin.components";

export const query = graphql`
    query ApplianceTemplate($code: String!) {
        productsJson(code: { eq: $code }) {
            ...ProductJsonTemplate
        }
    }
`;

export default function Appliance({ data }: PageProps<Queries.ApplianceTemplateQuery>) {
    const { t } = useTranslation("pages");
    const [productData, setProductData] = useState<IProduct>();
    const [stock, setStock] = useState();

    const servicesContext = useContext(ServicesContext);
    const productService = servicesContext?.magento.productService;

    const { currentLocale } = usePageLocalization();

    const single = data.productsJson as Queries.ProductJsonTemplateFragment;
    const category = single?.category?.split("/")[2];

    const downloadIcon = "M21.516,10.507h-4V4.5H11.5v6.007h-4l7.008,7.008ZM7.5,19.517v2H21.516v-2Z";

    const displayPrice = currentLocale !== "en_US";
    const document = single?.documents?.[0];

    useEffect(() => {
        if (single?.code) {
            productService.operations.getProduct(single?.code).then((product) => {
                if (product) {
                    setStock(product?.currentStock?.status as any);
                    setProductData(product);
                }
            });
        }
    }, [single?.code]);

    const getMetadata = (category: string, single: any): Metadata => {
        const metadata = {
            metaTitle: "",
            metaDescription: "",
        };

        const sku = single?.code as string;
        const productTitle = single?.title?.[currentLocale] as string;
        const subCategory = single?.category_localized?.[currentLocale]?.split("/")[3];

        if (category === "Accessories" || category === "Venting") {
            metadata.metaTitle = t("metadata:appliance.title", {
                SKU: sku,
                CATEGORY: subCategory,
            });
            metadata.metaDescription = t("metadata:appliance.description", {
                TITLE: productTitle,
                SKU: sku,
            });
        } else if (category === "Parts") {
            metadata.metaTitle = t("metadata:part.title", { SKU: sku });
            metadata.metaDescription = t("metadata:part.description", {
                TITLE: productTitle,
                SKU: sku,
            });
        } else if (category === "Options") {
            metadata.metaTitle = t("metadata:option.title", { SKU: sku });
            metadata.metaDescription = t("metadata:option.description", {
                TITLE: productTitle,
                SKU: sku,
            });
        } else {
            metadata.metaTitle = "";
            metadata.metaDescription = "";
        }

        return metadata;
    };

    const metadata = useMemo<Metadata>(
        () => getMetadata(category ?? "", single),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [currentLocale, category]
    );

    return (
        <MainLayout>
            <SEO title={metadata.metaTitle} description={metadata.metaDescription} />
            <div className="container">
                <div className="single-page">
                    <div className="single-infos">
                        <div className="left">
                            <ProductPreviewGallery product={single} />
                        </div>
                        <div className="right">
                            <h1 className="single-title">
                                <span>{single?.title?.[currentLocale] ?? "null"}</span>
                                <br />
                                <span className="code">{single?.code}</span>
                            </h1>
                            {single?.description?.[currentLocale] && (
                                <div className="description">
                                    {single.description[currentLocale]}
                                </div>
                            )}

                            {document && (
                                <div className="actions">
                                    <div className="download-manual">
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href={"/file/" + document[currentLocale]}
                                            className="download-text"
                                        >
                                            {t("download-instruct-manual")}
                                            <Icon
                                                d={downloadIcon}
                                                size="20"
                                                viewBox="0 0 14.016 17.019"
                                                transform="translate(-7.5 -4.5)"
                                            />
                                        </a>
                                    </div>
                                </div>
                            )}

                            <ProductPrice
                                currentPrice={productData?.currentPrice}
                                prefixClassName="appliance"
                            />
                            <ProductStock
                                type="medium"
                                currentStock={productData?.currentStock}
                                isCustomMade={false}
                            />

                            <div className="addToCart">
                                <AddItemButton
                                    type={"large"}
                                    sku={single.code!}
                                    availability={getAvailability(productData?.currentStock)}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
}
